import { Box, Chip, Stack, Tooltip } from "@mui/joy";
import DarkTypography from "../../../common/DarkTypography";
import DarkCard from "../../../common/DarkCard";
import IconButtonCTA from "../../../common/IconButtonCTA";
import { SDelete, SRefresh, SSmartphone } from "../../../../styles/StyledMUI";
import { colorTokens, roleCardTitleSx, textTokens } from "../../../../styles/globalStyles";
import type {
  MobileDevice,
  MobileDeviceStatus,
} from "../../../../api/mobileDevices/MobileDevice.typed";

type MobileDeviceCardProps = {
  device: MobileDevice;
  canPair: boolean;
  canDelete: boolean;
  onRepair: (device: MobileDevice) => void;
  onDelete: (device: MobileDevice) => void;
};

const STATUS_LABEL: Record<MobileDeviceStatus, string> = {
  paired: "Appairé",
  pending: "En attente",
  revoked: "Révoqué",
};

const STATUS_COLOR: Record<MobileDeviceStatus, "success" | "warning" | "danger"> = {
  paired: "success",
  pending: "warning",
  revoked: "danger",
};

function formatDateTime(value?: string | null): string {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const fieldLabelSx = {
  color: textTokens.color.subtle,
  fontWeight: 600,
  textTransform: "uppercase" as const,
  letterSpacing: "0.3px",
  fontSize: "0.65rem",
  mb: 0.3,
};

const fieldValueSx = {
  color: textTokens.color.bodyOnDark,
  fontSize: "0.8rem",
  lineHeight: 1.3,
};

const detailsGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
  gap: 1.25,
  flex: 1,
  minWidth: 0,
};

const detailsRowSx = {
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
  gap: 1,
  mt: 1,
  minWidth: 0,
};

export default function MobileDeviceCard(props: MobileDeviceCardProps) {
  const { device, canPair, canDelete, onRepair, onDelete } = props;
  const statusLabel = STATUS_LABEL[device.status];
  const statusColor = STATUS_COLOR[device.status];
  const showActions = device.status === "paired" || device.status === "pending";

  return (
    <DarkCard dense>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          minWidth: 0,
        }}
      >
        <SSmartphone sx={{ fontSize: 22, color: colorTokens.accent.primary, flexShrink: 0 }} />
        <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
          <DarkTypography level="h4" sx={{ ...roleCardTitleSx, mb: 0 }}>
            {device.label}
          </DarkTypography>
          {device.user_agent ? (
            <DarkTypography
              level="body-xs"
              colorVariant="muted"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {device.user_agent}
            </DarkTypography>
          ) : null}
        </Stack>
        <Chip size="sm" color={statusColor} variant="soft" sx={{ flexShrink: 0 }}>
          {statusLabel}
        </Chip>
      </Box>

      <Box sx={detailsRowSx}>
        <Box sx={detailsGridSx}>
          <Box sx={{ minWidth: 0 }}>
            <DarkTypography level="body-xs" sx={fieldLabelSx}>
              Appairé le
            </DarkTypography>
            <Box sx={fieldValueSx}>{formatDateTime(device.paired_at)}</Box>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <DarkTypography level="body-xs" sx={fieldLabelSx}>
              Dernière activité
            </DarkTypography>
            <Box sx={fieldValueSx}>{formatDateTime(device.last_seen_at)}</Box>
          </Box>
        </Box>

        {showActions ? (
          <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0, alignSelf: "flex-end" }}>
            {canPair ? (
              <Tooltip title="Réappairer">
                <IconButtonCTA
                  ctaVariant="accent"
                  variant="plain"
                  aria-label="Réappairer l'appareil"
                  onClick={() => onRepair(device)}
                >
                  <SRefresh />
                </IconButtonCTA>
              </Tooltip>
            ) : null}
            {canDelete ? (
              <Tooltip title="Supprimer">
                <IconButtonCTA
                  ctaVariant="danger"
                  variant="plain"
                  aria-label="Supprimer l'appareil"
                  onClick={() => onDelete(device)}
                >
                  <SDelete />
                </IconButtonCTA>
              </Tooltip>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </DarkCard>
  );
}
